import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { isSupervisorUser } from '../../../store/helpers';

const SECONDS_PER_MINUTE = 60;
export const SERVICE_BREACH_RATIO = 2.5;
export const SERVICE_BREACH_TYPE = 'active-service-time';

export const secondsToMinutes = (seconds) => Math.ceil(Math.max(0, Number(seconds) || 0) / SECONDS_PER_MINUTE);
export const formatElapsedSeconds = (seconds) => {
    const safeSeconds = Math.max(0, Math.floor(Number(seconds) || 0));

    if (safeSeconds < SECONDS_PER_MINUTE) {
        return '< 1 min';
    }

    const minutes = Math.floor(safeSeconds / SECONDS_PER_MINUTE);
    return `${minutes} min`;
};

const formatSecondsAsMinutes = (seconds) => {
    if (seconds == null || Number.isNaN(Number(seconds))) {
        return '-';
    }
    return `${secondsToMinutes(seconds)} min`;
};

const getLatestServiceDurationByService = (waitingTimes = []) => waitingTimes.reduce((acc, record) => {
    if (!record || !record.serviceId) {
        return acc;
    }
    const current = acc[record.serviceId];
    if (!current || Number(record.created || record.updated || 0) > Number(current.created || current.updated || 0)) {
        acc[record.serviceId] = record;
    }
    return acc;
}, {});

export const getActiveServiceSeconds = (ticket, now) => {
    const sessionStart = Number(ticket && ticket.sessionStart);
    if (!Number.isFinite(sessionStart) || sessionStart <= 0) {
        return 0;
    }
    return Math.max(0, now - sessionStart);
};

export const getServiceThresholdSeconds = (serviceDurationSeconds, ratio = SERVICE_BREACH_RATIO) => {
    const durationSeconds = Number(serviceDurationSeconds);
    if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
        return null;
    }
    return Math.ceil(durationSeconds * ratio);
};

export const isActiveServiceBreach = (ticket, serviceDurationSeconds, now) => {
    if (!ticket || Number(ticket.status) !== 1) {
        return false;
    }

    if (ticket.slaBreached && ticket.slaBreach && ticket.slaBreach.breachType === SERVICE_BREACH_TYPE) {
        return true;
    }

    const thresholdSeconds = getServiceThresholdSeconds(serviceDurationSeconds);
    return thresholdSeconds != null && getActiveServiceSeconds(ticket, now) > thresholdSeconds;
};

export const buildServiceWaitingRows = ({
    authenticatedUser = {},
    now,
    serviceWaitingTimes = [],
    services = [],
    tickets = [],
}) => {
    const latestServiceDurationByService = getLatestServiceDurationByService(serviceWaitingTimes);
    const canViewAllServices = isSupervisorUser(authenticatedUser);
    const visibleServices = canViewAllServices
        ? services
        : services.filter(service => (authenticatedUser.services || []).includes(service.id));

    return visibleServices.map((service) => {
        const waitingTickets = tickets.filter(ticket => Number(ticket.status) === 0 && ticket.serviceId === service.id);
        const activeTickets = tickets.filter(ticket => Number(ticket.status) === 1 && ticket.serviceId === service.id);
        const latestServiceDuration = latestServiceDurationByService[service.id];
        const serviceDurationSeconds = latestServiceDuration
            ? Number(latestServiceDuration.averageWaitingTime)
            : null;
        const serviceThresholdSeconds = getServiceThresholdSeconds(serviceDurationSeconds);
        const activeDurations = activeTickets.map(ticket => getActiveServiceSeconds(ticket, now));
        const longestActiveSeconds = activeDurations.length ? Math.max(...activeDurations) : 0;
        const breachedTickets = activeTickets.filter(ticket => isActiveServiceBreach(ticket, serviceDurationSeconds, now));
        const largestOverSeconds = breachedTickets.reduce((largest, ticket) => {
            const breach = ticket.slaBreach || {};
            const overSeconds = Number.isFinite(Number(breach.overSeconds))
                ? Number(breach.overSeconds)
                : getActiveServiceSeconds(ticket, now) - (serviceThresholdSeconds || 0);
            return Math.max(largest, overSeconds);
        }, 0);

        return {
            serviceId: service.id,
            serviceName: service.name,
            activeTickets,
            waitingTickets,
            waitingCount: waitingTickets.length,
            activeCount: activeTickets.length,
            breachedTickets,
            breachedCount: breachedTickets.length,
            serviceDurationSeconds,
            serviceThresholdSeconds,
            longestActiveSeconds,
            largestOverSeconds,
        };
    }).sort((a, b) => {
        if (b.breachedCount !== a.breachedCount) {
            return b.breachedCount - a.breachedCount;
        }
        if (b.largestOverSeconds !== a.largestOverSeconds) {
            return b.largestOverSeconds - a.largestOverSeconds;
        }
        if (b.activeCount !== a.activeCount) {
            return b.activeCount - a.activeCount;
        }
        if (b.waitingCount !== a.waitingCount) {
            return b.waitingCount - a.waitingCount;
        }
        if (b.longestActiveSeconds !== a.longestActiveSeconds) {
            return b.longestActiveSeconds - a.longestActiveSeconds;
        }
        return a.serviceName.localeCompare(b.serviceName);
    });
};

function ServiceWaitingTimes() {
    const [now, setNow] = useState(Math.floor(Date.now() / 1000));
    const { authenticatedUser } = useSelector(state => state.auth);
    const { serviceWaitingTimes, services } = useSelector(state => state.services);
    const { tickets } = useSelector(state => state.tickets);

    useEffect(() => {
        const interval = setInterval(() => {
            setNow(Math.floor(Date.now() / 1000));
        }, 30000);
        return () => clearInterval(interval);
    }, []);

    const rows = useMemo(() => buildServiceWaitingRows({
        authenticatedUser,
        now,
        serviceWaitingTimes,
        services,
        tickets,
    }), [authenticatedUser, now, serviceWaitingTimes, services, tickets]);

    return (
        <div className="supervisorPanel supervisorPanelLarge">
            <div className="supervisorPanelHeader">
                <div className="titleBlock">
                    <h4>Service Load</h4>
                </div>
            </div>
            <table className="supervisorTable">
                <thead>
                    <tr>
                        <th>Service</th>
                        <th className="supervisorTableNumber supervisorTableSmall">Waiting</th>
                        <th className="supervisorTableNumber supervisorTableSmall">Active</th>
                        <th className="supervisorTableNumber supervisorTableMedium">Avg Service</th>
                        <th className="supervisorTableNumber supervisorTableMedium">SLA Limit</th>
                        <th className="supervisorTableNumber supervisorTableMedium">Longest Active</th>
                        <th className="supervisorTableNumber supervisorTableSmall">Breached</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(row => (
                        <tr key={row.serviceId}>
                            <td className="supervisorTableLabel">
                                {row.serviceName}
                                {row.breachedCount ? (
                                    <span className="supervisorBreachBadge">
                                        SLA breached
                                    </span>
                                ) : null}
                            </td>
                            <td className="supervisorTableNumber">{row.waitingCount}</td>
                            <td className="supervisorTableNumber">{row.activeCount}</td>
                            <td className="supervisorTableNumber">{formatSecondsAsMinutes(row.serviceDurationSeconds)}</td>
                            <td className="supervisorTableNumber">{formatSecondsAsMinutes(row.serviceThresholdSeconds)}</td>
                            <td className="supervisorTableNumber supervisorTableStrong">
                                {row.activeCount ? formatElapsedSeconds(row.longestActiveSeconds) : '-'}
                            </td>
                            <td className="supervisorTableNumber supervisorTableStrong">{row.breachedCount}</td>
                        </tr>
                    ))}
                    {!rows.length ? (
                        <tr>
                            <td className="supervisorTableEmpty" colSpan="7">
                                No service load data available
                            </td>
                        </tr>
                    ) : null}
                </tbody>
            </table>
        </div>
    );
}

export default ServiceWaitingTimes;
